<template>
  <b-overlay
    :show="showOverlay"
    rounded="sm"
  >
    <b-modal
      id="modal-claim-approve-offer"
      ref="modalClaimApproveOffer"
      cancel-variant="outline-secondary"
      ok-disabled
      cancel-disabled
      cancel-title="Close"
      centered
      title="Claim accepteren"
      :hide-footer="true"
      :no-close-on-backdrop="false"
    >
      <validation-observer
        #default="{ handleSubmit }"
        ref="refClaimApproveFormObserver"
      >
        <b-form
          @submit.prevent="handleSubmit(handelClaimApprovalRequest)"
        >
          <b-row>
            <b-col
              cols="12"
              md="12"
            >
              <validation-provider
                #default="validationContext"
                name="approvalStatus"
                rules="required_if:required_if_add_company_or_consumer_is,1"
              >
                <b-form-group
                  label="Type acceptatie"
                  label-for="approvalStatus"
                  :state="getValidationState(validationContext)"
                >
                  <v-select
                    id="approvalStatus"
                    v-model="claimApprovalObject.status"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="approveStatusOptions"
                    :reduce="val => val.value"
                    :clearable="true"
                  />
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
            >
              <validation-provider
                #default="validationContext"
                name="claimNote"
                rules="required"
              >
                <b-form-group>
                  <label>Notities</label>
                  <b-form-textarea
                    id="claimNote"
                    v-model="claimApprovalObject.claimNote"
                    rows="2"
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
              >
                <b-spinner
                  v-if="showClaimApprovalSpinner"
                  small
                  class="mr-1"
                />
                Aanvragen
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <section id="offer">
      <b-row>
        <b-col cols="12">
          <b-card
            no-body
          >
            <div class="m-2">
              <!-- Table Top -->
              <b-row>
                <!-- Per Page -->
                <b-col
                  cols="12"
                  md="6"
                  class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
                >
                  <label>Records</label>
                  <v-select
                    v-model="perPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="perPageOptions"
                    :clearable="false"
                    class="per-page-selector d-inline-block ml-50 mr-1"
                  />
                </b-col>
                <b-col
                  class="d-flex align-items-center justify-content-end"
                  cols="12"
                  md="6"
                />
              </b-row>
            </div>
            <b-table
              ref="refClaimListTable"
              :items="fetchClaim"
              responsive
              :fields="tableColumns"
              primary-key="id"
              :sort-by.sync="sortBy"
              show-empty
              empty-text="Geen overeenkomende records gevonden"
              :sort-desc.sync="isSortDirDesc"
              class="position-relative"
            >
              <!-- Column: Id -->
              <template #cell(id)="data">
                <b-link
                  :to="{ name: 'app-order-detail', params: { id: data.item.id }}"
                  class="font-weight-bold"
                  style="color:#ff9f43"
                >
                  #{{ data.item.id }}
                </b-link>
              </template>
              <!-- Column: Client -->
              <template #cell(policyHolderRelation)="data">
                <b-media vertical-align="center">
                  <template #aside>
                    <b-avatar
                      size="32"
                      src=""
                      :text="avatarText(getFullName(data.item.policyHolderRelation))"
                      style="background:#ff9f43"
                    />
                  </template>
                  <span class="font-weight-bold d-block text-nowrap">
                    {{ getFullName(data.item.policyHolderRelation) }}
                  </span>
                  <small class="text-muted">{{ data.item.policyHolderRelation.email }}</small>
                </b-media>
              </template>
              <!-- Column: Issued Date -->
              <template #cell(claimDateOfDeath)="data">
                <span class="text-nowrap">
                  {{ data.value ? formatDateTime(data.value) : '' }}
                </span>
              </template>
              <!-- Column: Issued Date -->
              <template #cell(timestamp)="data">
                <span class="text-nowrap">
                  {{ formatDateTime(data.value) }}
                </span>
              </template>
              <!-- Column: Id -->
              <template #cell(product)="data">
                {{ data.item.product.name }}
              </template>
              <template #cell(actions)="data">
                <div class="text-nowrap">
                  <feather-icon
                    v-if="data.item.status === 110"
                    :id="`claim-row-${data.item.id}-approval-icon`"
                    class="cursor-pointer mr-1"
                    icon="MapPinIcon"
                    size="16"
                    style="color:rgb(40,199,111);"
                    @click="openApproveModal(data.item.id)"
                  />
                  <feather-icon
                    v-if="data.item.status === 111 || data.item.status === 112 || data.item.status === 113"
                    :id="`claim-row-${data.item.id}-approval-client-icon`"
                    class="cursor-pointer mr-1"
                    icon="UsersIcon"
                    size="16"
                    style="color:rgb(40,199,111);"
                    @click="confirmClientApproval(data.item.id)"
                  />
                  <feather-icon
                    :id="`claim-row-${data.item.id}-disapprive-icon`"
                    class="cursor-pointer"
                    icon="XCircleIcon"
                    size="16"
                    style="color:rgb(234,84,85) !important;"
                    @click="confirmClaimDispprove(data.item.id)"
                  />
                </div>
              </template>
            </b-table>
            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span class="text-muted">Records {{ dataMeta.from }} tot {{ dataMeta.to }} van totaal {{
                    dataMeta.of
                  }} records zichtbaar</span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                >
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalClaims"
                    :per-page="perPage"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </section>
  </b-overlay>
</template>
<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BLink,
  BOverlay,
  BFormInvalidFeedback,
  BFormGroup,
  BForm,
  BButton,
  BPagination,
  BSpinner,
  BMedia,
  BAvatar,
  BFormTextarea,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import useClaimList from '@/views/pages/claim/useClaimList'
import { formatDateTime } from '@core/utils/utils'
import claimStoreModule from '@/views/pages/claim/claimStoreModule'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import Ripple from 'vue-ripple-directive'
import formValidation from '@core/comp-functions/forms/form-validation'
import { decimal, required } from '@core/utils/validations/validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import errorAndSuccesDefaultMessage from '@core/app-config/errorAndSuccesDefaultMessage'
import { avatarText } from '@core/utils/filter'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BLink,
    vSelect,
    BOverlay,
    BFormInvalidFeedback,
    BFormGroup,
    BForm,
    BButton,
    BPagination,
    BSpinner,
    BMedia,
    BAvatar,
    BFormTextarea,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      claimApprovalObject: {
        claimNote: '',
        status: null,
        id: null,
      },
      showClaimApprovalSpinner: false,
    }
  },
  methods: {
    fetchStatistics() {
      store
        .dispatch('app-menu-statistics/fetchMenuStatistics')
        .then(response => {
          this.$store.commit('app/UPDATE_MENU_STATISTICS', response.data)
        })
    },
    openApproveModal(id) {
      this.claimApprovalObject.id = id
      this.$refs.modalClaimApproveOffer.show()
    },
    getFullName(data) {
      let fullName = ''

      if (data) {
        if (data.middleName && data.middleName.length > 0) {
          fullName = `${data.firstName} ${data.middleName} ${data.lastName}`
        } else {
          fullName = `${data.firstName} ${data.lastName}`
        }
      }

      return fullName
    },
    confirmClientApproval(orderID) {
      this.$swal({
        title: 'Weet je het zeker?',
        text: 'U kunt dit niet meer terugdraaien!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, claim accepteren!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.isConfirmed) {
          store.dispatch('app-claim/updateClaim', { id: orderID, status: 120 })
            .then(() => {
              this.fetchStatistics()
              this.refetchClaimData()
              this.$swal({
                icon: 'success',
                title: 'Claim!',
                text: 'De claim is geaccepteerd.',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Claim accepteren',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                  text: `${errorAndSuccesDefaultMessage.saveDataError}`,
                },
              })
            })
        }
      })
    },
    confirmClaimDispprove(orderID) {
      this.$swal({
        title: 'Weet je het zeker?',
        text: 'U kunt dit niet meer terugdraaien!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, claim afwijzen!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.isConfirmed) {
          store.dispatch('app-claim/updateClaim', { id: orderID, status: 114 })
            .then(() => {
              this.fetchStatistics()
              this.refetchClaimData()
              this.$swal({
                icon: 'success',
                title: 'Claim!',
                text: 'De claim is afgewezen.',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Claim afwijzen',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                  text: `${errorAndSuccesDefaultMessage.saveDataError}`,
                },
              })
            })
        }
      })
    },
    handelClaimApprovalRequest() {
      this.showClaimApprovalSpinner = true
      store.dispatch('app-claim/updateClaim', this.claimApprovalObject)
        .then(() => {
          this.fetchStatistics()
          this.showClaimApprovalSpinner = false
          this.$refs.modalClaimApproveOffer.hide()
          this.refetchClaimData()
          this.$swal({
            icon: 'success',
            title: 'Claim!',
            text: 'De acceptatie van de claim is verwerkt.',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
        })
        .catch(() => {
          this.showClaimApprovalSpinner = false
          this.$refs.modalClaimApproveOffer.hide()
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Claim accepteren',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: `${errorAndSuccesDefaultMessage.saveDataError}`,
            },
          })
        })
    },
  },
  setup() {
    const CLAIM_APP_STORE_MODULE_NAME = 'app-claim'

    // Register module
    if (!store.hasModule(CLAIM_APP_STORE_MODULE_NAME)) store.registerModule(CLAIM_APP_STORE_MODULE_NAME, claimStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CLAIM_APP_STORE_MODULE_NAME)) store.unregisterModule(CLAIM_APP_STORE_MODULE_NAME)
    })

    const {
      refFormObserver,
      getValidationState,
    } = formValidation(() => {})

    const {
      fetchClaim,
      tableColumns,
      perPage,
      currentPage,
      totalClaims,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refClaimListTable,
      refetchClaimData,
      showOverlay,
      approveStatusOptions,
    } = useClaimList()

    return {
      fetchClaim,
      tableColumns,
      perPage,
      currentPage,
      totalClaims,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refClaimListTable,
      refetchClaimData,
      showOverlay,
      refFormObserver,
      getValidationState,
      required,
      decimal,
      avatarText,
      formatDateTime,
      approveStatusOptions,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }

  .categorie-table{
    .description{
      width:30%;
    }
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
